import { useEffect, useState } from "react";
import { Sparkles, X, Mail, Phone, ArrowRight, ShieldCheck } from "lucide-react";
import { submitLead, track } from "../lib/analytics";
import { MailDeliveryHint } from "./MailDeliveryHint";

const SHOWN_KEY = "alphalane_lead_magnet_state"; // "shown" | "dismissed" | "captured"
const DISMISSED_AT_KEY = "alphalane_lead_magnet_dismissed_at";
// Re-show the popup if the visitor dismissed it more than this many ms ago.
// Captured leads are NEVER re-prompted — they already have the report.
const REDISPLAY_AFTER_MS = 60 * 60 * 1000; // 1 hour

export const LeadMagnet = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const state = localStorage.getItem(SHOWN_KEY);

    // "captured" → never bug them again
    if (state === "captured") return;

    // "dismissed" → only suppress while we are still inside the cool-off window
    if (state === "dismissed") {
      const ts = Number(localStorage.getItem(DISMISSED_AT_KEY));
      if (ts && Date.now() - ts < REDISPLAY_AFTER_MS) return;
      // Cool-off elapsed → wipe the suppress flag and let it fire again
      localStorage.removeItem(SHOWN_KEY);
      localStorage.removeItem(DISMISSED_AT_KEY);
    }

    let shown = false;
    const show = (reason) => {
      if (shown) return;
      shown = true;
      setOpen(true);
      track("lead_magnet_shown", { meta: { reason } });
    };

    // Strategy: show after 15s OR 30% scroll OR exit-intent (mouse leaves top)
    const t = setTimeout(() => show("timer_15s"), 15000);

    const onScroll = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      if (total > 0 && window.scrollY / total > 0.3) {
        show("scroll_30");
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    const onMouseLeave = (e) => {
      if (e.clientY < 6) show("exit_intent");
    };
    document.addEventListener("mouseleave", onMouseLeave);

    return () => {
      clearTimeout(t);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("mouseleave", onMouseLeave);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(SHOWN_KEY, "dismissed");
    localStorage.setItem(DISMISSED_AT_KEY, String(Date.now()));
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    const lead = await submitLead(email, name, {
      phone: phone ? phone.trim() : null,
    });
    setLoading(false);
    if (lead) {
      localStorage.setItem(SHOWN_KEY, "captured");
      setDone(true);
      setTimeout(() => setOpen(false), 3500);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="lead-magnet"
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center px-3 pb-3 sm:p-6 overflow-y-auto"
    >
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={dismiss} />
      {/* Sticky close button — always visible at the top-right of the
          viewport even when the card is taller than the screen on mobile. */}
      <button
        onClick={dismiss}
        aria-label="Schliessen"
        data-testid="lead-magnet-close"
        className="fixed top-3 right-3 sm:top-5 sm:right-5 z-[81] h-10 w-10 rounded-full bg-white/95 backdrop-blur shadow-lg ring-1 ring-slate-200 flex items-center justify-center text-slate-700 hover:bg-white hover:text-slate-900 transition-colors"
      >
        <X className="h-5 w-5" />
      </button>
      <div className="relative al-card w-full max-w-md p-7 sm:p-8 shadow-2xl shadow-purple-500/25 my-3 sm:my-0 max-h-[95vh] overflow-y-auto">

        <span className="al-tag"><Sparkles className="h-3 w-3" /> Im Wert von CHF 49.– · Sofort per Mail</span>

        {!done ? (
          <>
            <h2 className="mt-4 font-display font-bold text-2xl sm:text-[26px] leading-tight">
              Der Alphalane <span className="al-grad-text">Insider-Report</span>.
            </h2>
            <p className="mt-3 text-[14px] text-slate-600 leading-relaxed">
              <strong>5 unbequeme Wahrheiten</strong> über automatisiertes Trading — plus die <strong>12-Punkte-Checkliste</strong>, mit der du jeden seriösen Anbieter prüfst.
            </p>
            <ul className="mt-4 space-y-2 text-[13px] text-slate-700">
              <li className="flex items-start gap-2">
                <span className="mt-0.5 text-[var(--al-purple)] font-bold">›</span>
                <span>Warum <strong>90 % aller Privatanleger</strong> Geld verlieren — und wie du das vermeidest</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5 text-[var(--al-purple)] font-bold">›</span>
                <span>Die <strong>4 Bausteine</strong> eines professionellen Trading-Algorithmus</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5 text-[var(--al-purple)] font-bold">›</span>
                <span><strong>Risiko-Management</strong> wie Profis — Position Sizing, Drawdown-Trigger, Korrelations-Cap</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-0.5 text-[var(--al-purple)] font-bold">›</span>
                <span>Die <strong>12-Punkte-Checkliste</strong> — wähle nur Anbieter, die ≥ 9 davon erfüllen</span>
              </li>
            </ul>
            <p className="mt-4 text-[12px] text-slate-500 italic">
              7 Seiten geballtes Wissen · 12 Min Lesezeit · kein Marketing-Bla
            </p>

            <div
              data-testid="lead-magnet-price"
              className="mt-4 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 px-4 py-3 text-white flex items-center justify-between gap-3 shadow-lg shadow-emerald-500/25"
            >
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">Normalpreis</div>
                <div className="al-slash text-lg font-display font-bold opacity-95">CHF 49.–</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">Für dich heute</div>
                <div className="text-2xl font-display font-extrabold">CHF 0.–</div>
              </div>
            </div>

            <form onSubmit={submit} className="mt-4 space-y-3">
              <input
                data-testid="lead-magnet-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Vorname (optional)"
                className="al-input"
              />
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  data-testid="lead-magnet-email"
                  required
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="deine@email.com"
                  className="al-input !pl-10"
                />
              </div>
              <div className="relative">
                <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  data-testid="lead-magnet-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Telefon (optional, z.B. +41 79 …)"
                  autoComplete="tel"
                  inputMode="tel"
                  className="al-input !pl-10"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="lead-magnet-submit"
                className="al-cta w-full justify-center disabled:opacity-60"
              >
                {loading ? "Wird gesendet..." : "Report gratis sichern"} <ArrowRight className="h-4 w-4" />
              </button>
            </form>

            <div className="mt-4 flex items-center gap-1.5 text-[11px] text-slate-500">
              <ShieldCheck className="h-3 w-3 text-[var(--al-purple)]" />
              Kein Spam. Jederzeit abbestellbar. Datenschutz garantiert.
            </div>
          </>
        ) : (
          <div className="py-6 text-center">
            <div className="h-14 w-14 rounded-full al-grad-bg mx-auto flex items-center justify-center text-white text-2xl font-display font-bold">✓</div>
            <h3 className="mt-4 font-display font-semibold text-xl">Auf dem Weg in dein Postfach.</h3>
            <p className="mt-2 text-sm text-slate-600">Du solltest die Mail innerhalb von 1–2 Minuten erhalten.</p>
            <MailDeliveryHint testid="leadmagnet-mail-hint" />
          </div>
        )}
      </div>

      <style>{`
        .al-input { width: 100%; padding: 12px 14px; border-radius: 12px; border: 1px solid var(--al-line); background: white; font-family: 'Manrope', sans-serif; font-size: 15px; color: var(--al-ink); transition: border-color 200ms, box-shadow 200ms; }
        .al-input:focus { outline: none; border-color: var(--al-purple); box-shadow: 0 0 0 4px rgba(139,92,246,0.12); }
      `}</style>
    </div>
  );
};
